import Icon from "@ews/react-icons";
import { useL10n } from "@ews/react-localization-context";
import { IonInputCustomEvent } from "@ionic/core";
import { IonButton, IonButtons, IonContent, IonHeader, IonItem, IonLabel, IonModal, IonPopover, IonSearchbar, IonTitle, IonToolbar } from "@ionic/react";
import React, { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useIsMobile } from "../Responsive";
import SettingsTextInput, { selectInputValue } from "../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { ReactClickEvent } from "../types";
import styleTypeahead from "./Typeahead.module.scss";
import { Item, TypeaheadContainerProps, TypeaheadProps } from "./types";

function pxToPercent(childSize: number, parentSize: number): number
{
    return Math.round((childSize / parentSize) * 100);
}




const TypeAheadContainer: React.FC<TypeaheadContainerProps> = ({
    id,
    children,
    className,
    modalRef,
    isOpen = false,
    view,
    side,
    listLength,
    onDismiss,
    useOffsetY
}) =>
{
    const mobileView = useIsMobile();
    function popoverWindowPosition(popover: HTMLIonPopoverElement, view: number, cb?: (height: number) => void)
    {
        if (!popover.children[0]) return;
        const popoverWindow = popover.children[0] as HTMLDivElement;
        popoverWindow.style.maxHeight = `350px`;
        const windowHeight = popoverWindow.clientHeight;
        const offsetY = windowHeight < 350 ? windowHeight + 48 : 350;
        cb && cb(offsetY);
    }

    const [offset, setOffset] = useState<number>(0);


    useEffect(() =>
    {
        if (isOpen) {
            modalRef?.current?.present({
                target: document.querySelector(`#open-typeahead-${id}`)
            } as CustomEvent);
        }

        // (modalRef?.current as HTMLIonPopoverElement).event = {
        //     target: document.querySelector(`#open-typeahead-${id}`)
        // };

    });
    useEffect(() =>
    {
        if (mobileView) return;
        if (side !== "top") return;
        if (modalRef?.current) {
            if (modalRef.current.children.length) {

                const popover = modalRef.current as HTMLIonPopoverElement;
                popoverWindowPosition(popover, view, setOffset);
            }
        }
    }, [listLength, mobileView, modalRef, side, view]);

    const initialBreakpoint = .5;

    return mobileView ?
        <IonModal
            showBackdrop={true}
            keepContentsMounted
            keyboardClose={true}
            className={className}
            ref={modalRef as RefObject<HTMLIonModalElement>}
            initialBreakpoint={initialBreakpoint}
            breakpoints={[0, 0.5]}
            onDidDismiss={() => { onDismiss && onDismiss(); }}

        >
            <div style={{ height: `${(initialBreakpoint * 100) - initialBreakpoint}dvh`, overflow: "hidden" }}>
                {children}
            </div>
        </IonModal>
        :
        <IonPopover
            ref={modalRef as RefObject<HTMLIonPopoverElement>}
            keyboardClose={false}
            backdropDismiss={true}
            side={side}
            className={`${className}`}
            size="cover"
            id={`popover-${id}`}
            showBackdrop={false}
            onDidDismiss={() =>
            {
                onDismiss && onDismiss();
            }}
            onDidPresent={(e) =>
            {
                if (mobileView) return;
                if (side !== "top") return;
                const popover = e.target as HTMLIonPopoverElement;
                popoverWindowPosition(popover, view, (height) => setOffset(height));
            }}
            style={useOffsetY && side === "top" ? { "--offset-y": `-${offset}px` } : {}}
        >
            {children}
        </IonPopover>;
};


const TypeAhead = <T extends Record<string, any> = Record<string, any>,>({
    id,
    text,
    list = [],
    onFilter,
    onChange,
    onClear,
    showResetButton,
    debounce,
    lines,
    disabled,
    required,
    value,
    color,
    endText,
    startText,
    inputType = 'text',
    placeholder,
    inputRef,
    useOffsetY = false
}: TypeaheadProps<T>) =>
{
    const searchbarId = `typeahead-IonSearchbar-${id}`;
    const mobileView = useIsMobile();
    const { translate: t } = useL10n();

    const modalRef = useRef<HTMLIonPopoverElement>(null);
    const searchbarRef = useRef<HTMLIonSearchbarElement>(null);

    const [searchValue, setSearchValue] = useState(value || '');

    const [typeaheadList, setTypeaheadList] = useState<Item<T>[]>([]);
    const view = useRef<number>(0);
    const clickedOnYPosition = useRef<number>(0);
    const popoverSide = useRef<"top" | "bottom">("bottom");
    const containerHeight = useRef<number>(0);
    const innerInputRef = useRef<HTMLIonInputElement>(null);


    const getRef = () =>
    {
        if (mobileView) {
            return searchbarRef;
        }
        else if (inputRef) {
            return inputRef;
        }
        return innerInputRef;

    };

    const setFocus = () =>
    {
        const ref = getRef();
        ref.current?.setFocus();
    };


    const handleClose = async (value: string | number | undefined, focus: boolean = false) =>
    {
        modalRef.current?.dismiss();
        //setSearchValue(value || "");
        setTypeaheadList([]);
        if (focus) setTimeout(() => setFocus());
    };

    const _setSelectInputValue = () =>
    {
        const ref = getRef();

        selectInputValue(ref);
    };


    const handleOpen = (e: IonInputCustomEvent<FocusEvent>) =>
    {

        const element: HTMLIonInputElement = (e.target as HTMLIonInputElement);

        if (element.tagName === 'ION-INPUT') {
            setFocus();
        }
    };



    const restore = (value: string | number | undefined, focus: boolean = false) =>
    {
        //setSearchValue(value || "");
        handleClose(value, focus);
    };

    const loadTypeaheadList = async (value: string) =>
    {
        const results = await onFilter(value);
        setTypeaheadList(results || []);
    };

    const handleClick = (item: Item<T>) =>
    {

        if (disabled) return;
        const { key, data } = item;
        const value = data[key];
        setSearchValue(value);
        //setTypeaheadList([]);
        if (onChange) onChange(data);
        else loadTypeaheadList(value);
        handleClose("");
    };


    const handleContainerClick = () =>
    {

        if (disabled) return;
        loadTypeaheadList("");
        _setSelectInputValue();

    };

    useEffect(() =>
    {
        if (!value) setSearchValue("");
        else setSearchValue(value + "");

    }, [value]);

    useEffect(() =>
    {
        if (mobileView && typeaheadList.length && searchbarRef.current) {
            setTimeout(() =>
            {

                if (searchbarRef.current) {

                    searchbarRef.current?.setFocus();

                }
            }, 250);
        }
    }, [mobileView, typeaheadList.length]);

    const calcModalDirection = (e: ReactClickEvent) =>
    {
        const { clientY, view: window } = e!.nativeEvent as PointerEvent;
        const screenHeight = window?.innerHeight || 0;

        view.current = screenHeight;
        clickedOnYPosition.current = clientY;
        containerHeight.current = screenHeight;
        const heightPercent = pxToPercent(clientY, screenHeight);

        popoverSide.current = heightPercent >= 55 ? "top" : "bottom";

    };

    return <>
        <SettingsTextInput
            id={`open-typeahead-${id}`}
            debounce={debounce}
            textAlign={mobileView ? "right" : "left"}
            color={color}
            endText={endText}
            startText={startText}
            disabled={disabled}
            required={required}
            lines={lines}
            inputRef={inputRef || innerInputRef}
            icon={<Icon icon="search" />}
            onKeyDown={(e) =>
            {
                if (e.keyCode === 27 || e.keyCode === 9) {
                    restore(searchValue || "", e.keyCode === 27);
                }
            }}
            onIconClick={(e: ReactClickEvent) =>
            {

                calcModalDirection(e);
                setFocus();
                loadTypeaheadList("");
            }}
            onClick={(e) =>
            {
                calcModalDirection(e as unknown as ReactClickEvent);
                handleContainerClick();
            }}
            text={text}
            placeholder={placeholder}
            inputType={inputType}
            value={searchValue}
            //onClick={(e) => handleOpen(e as unknown as ReactClickEvent)}
            //onFocus={() => loadTypeaheadList(searchValue)}
            onFocus={(e) =>
            {
                if (disabled) return;
                handleOpen(e);
            }}
            //onBlur={(e) => handleClose()}
            onEnter={(value) => setSearchValue(value)}
            onChange={(value) =>
            {
                if (disabled) return;
                loadTypeaheadList(value);
                setSearchValue(value || "");
            }}
        />
        {
            !disabled ? <TypeAheadContainer
                isOpen={Boolean(typeaheadList.length)}
                id={id || ''}
                className={styleTypeahead.typeAhead}
                modalRef={modalRef}
                showBackdrop={true}
                view={view.current}
                side={popoverSide.current}
                containerHeight={containerHeight.current}
                listLength={typeaheadList.length}
                onDismiss={() => handleClose(searchValue || "")}
                useOffsetY={useOffsetY}
            //className={`${styleTypeahead.backdrop} ${styleTypeahead.modal}`}

            >
                {mobileView && <IonHeader>
                    <IonToolbar color="dark">
                        <IonTitle>{text}</IonTitle>
                        <IonButtons slot="end">
                            <IonButton disabled={disabled} onClick={() => handleClose(undefined)}>
                                <Icon icon={"close"} />
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>}

                {mobileView && <IonSearchbar
                    ref={searchbarRef}
                    data-testid="typeahead-searchbar"
                    className={searchbarId}
                    value={`${searchValue}`}
                    onClick={_setSelectInputValue}
                    onIonChange={({ detail }) => setSearchValue(detail.value || '')}
                    onIonClear={() =>
                    {
                        if (disabled) return;
                        setSearchValue('');
                        loadTypeaheadList('');
                    }}
                    onIonInput={({ detail }) =>
                    {
                        if (disabled) return;

                        setSearchValue(detail.value || "");
                        loadTypeaheadList(detail.value || "");

                    }}
                    onAbort={() => handleClose}

                />}

                <IonContent
                    className={styleTypeahead.list}
                    data-testid="typeahead-content"
                >
                    {typeaheadList.length ? typeaheadList.map((item, index) =>
                    {
                        const { key, data } = item;
                        return <IonItem
                            data-testid="typeahead-chooice-item"
                            key={index}
                            button={true}
                            detail={false}
                            lines="full"
                            color={'secondary'}
                            onClick={async () =>
                            {
                                if (disabled) return;
                                handleClick(item);
                            }
                            }
                        >
                            <span className={styleTypeahead.textContainer}>
                                <IonLabel>
                                    {data[key]}
                                </IonLabel>
                            </span>
                        </IonItem>;
                    })
                        : <IonItem
                            detail={false}
                            lines="full"
                            color={'secondary'}
                        >
                            <span className={styleTypeahead.textContainer}>
                                <IonLabel>
                                    {t("[REACT_NO_CONTENT]")}
                                </IonLabel>
                            </span>
                        </IonItem>}
                </IonContent>

            </TypeAheadContainer > :
                null
        }
    </>;
};

export default TypeAhead;
